class SalonVideoTestimonialsCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.salon-video-testimonials__viewport');
    this.slides = Array.from(this.querySelectorAll('[data-salon-video-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-salon-video-dot]'));
    this.videos = Array.from(this.querySelectorAll('video'));
    this.index = 0;
    this.interval = Number(this.dataset.autoplayInterval) || 5000;
    this.autoScroll = this.dataset.autoScroll === 'true';
    this.hasAutoplayedFirstVideo = false;

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.scrollToSlide(index);
        this.restart();
      });
    });

    this.videos.forEach((video) => {
      video.addEventListener('play', this.stop);
      video.addEventListener('pause', this.handleVideoPause);
      video.addEventListener('ended', this.handleVideoEnded);
    });

    this.viewport?.addEventListener('scroll', this.handleScroll, { passive: true });
    this.viewport?.addEventListener('scrollend', this.handleScrollEnd);

    if (this.viewport && typeof ResizeObserver !== 'undefined') {
      this.viewportResizeObserver = new ResizeObserver(() => {
        this.syncSlideWidths();
        requestAnimationFrame(() => {
          this.scrollToSlide(this.index, { behavior: 'auto' });
        });
      });
      this.viewportResizeObserver.observe(this.viewport);
    }

    this.syncSlideWidths();
    requestAnimationFrame(() => {
      this.syncSlideWidths();
      this.scrollToSlide(this.index, { behavior: 'auto' });
    });

    this.setActive(0);
    if (!this.autoplayFirstVideo() && this.autoScroll) this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.viewportResizeObserver?.disconnect();
    this.viewportResizeObserver = null;
    this.viewport?.removeEventListener('scroll', this.handleScroll);
    this.viewport?.removeEventListener('scrollend', this.handleScrollEnd);

    this.videos.forEach((video) => {
      video.removeEventListener('play', this.stop);
      video.removeEventListener('pause', this.handleVideoPause);
      video.removeEventListener('ended', this.handleVideoEnded);
    });
  }

  syncSlideWidths() {
    if (!this.viewport) return;
    const w = this.viewport.clientWidth;
    if (w <= 0) return;
    this.viewport.style.setProperty('--salon-vt-slide-px', `${w}px`);
  }

  autoplayFirstVideo() {
    if (this.hasAutoplayedFirstVideo) return false;

    const firstVideo = this.slides[0]?.querySelector('video');
    if (!firstVideo || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;

    this.hasAutoplayedFirstVideo = true;
    this.stop();
    firstVideo.muted = true;
    firstVideo.playsInline = true;
    firstVideo.play().catch(() => {
      this.start();
    });
    return true;
  }

  start = () => {
    if (!this.autoScroll) return;
    if (this.slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (this.timer) return;
    this.timer = window.setInterval(() => this.scrollToSlide((this.index + 1) % this.slides.length), this.interval);
  };

  stop = () => {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  };

  restart() {
    this.stop();
    this.start();
  }

  handleVideoPause = (event) => {
    if (event.target.ended) return;
    this.start();
  };

  handleVideoEnded = () => {
    this.pauseVideos({ reset: true });
    this.scrollToSlide((this.index + 1) % this.slides.length);
    this.start();
  };

  handleScroll = () => {
    window.clearTimeout(this.scrollTimer);
    this.scrollTimer = window.setTimeout(this.handleScrollEnd, 120);
  };

  handleScrollEnd = () => {
    const nextIndex = this.getClosestSlideIndex();
    if (nextIndex === this.index) return;

    this.setActive(nextIndex);
    this.pauseVideos({ reset: false });
  };

  getClosestSlideIndex() {
    if (!this.viewport || this.slides.length === 0) return 0;

    const w = this.viewport.clientWidth;
    if (w <= 0) return 0;

    const ratio = this.viewport.scrollLeft / w;
    return Math.min(this.slides.length - 1, Math.max(0, Math.round(ratio)));
  }

  scrollToSlide(index, options = {}) {
    const slide = this.slides[index];
    if (!this.viewport || !slide) return;

    this.pauseVideos({ reset: false });
    this.setActive(index);

    const w = this.viewport.clientWidth;
    const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    const left = Math.min(Math.max(0, index * w), maxScroll);

    this.viewport.scrollTo({
      left,
      behavior: options.behavior || 'smooth',
    });
  }

  setActive(index) {
    this.index = index;

    this.dots.forEach((dot, dotIndex) => {
      if (dotIndex === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  pauseVideos(options = {}) {
    this.videos.forEach((video) => {
      if (!video.paused) video.pause();
      if (options.reset && video.readyState > 0) video.currentTime = 0;
    });
  }
}

if (!customElements.get('salon-video-testimonials-carousel')) {
  customElements.define('salon-video-testimonials-carousel', SalonVideoTestimonialsCarousel);
}
